import React from 'react';
import { FaFolder, FaPlus, FaEdit, FaTrash, FaBookmark, FaArchive } from 'react-icons/fa';
import { FaChartGantt } from 'react-icons/fa6';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../ui/card';

function ProjectGroupCard({ 
  projectGroup, 
  viewMode = 'card', // 'card' or 'list'
  onEdit,
  onDelete,
  onAddProject,
  onToggleFavorite,
  onToggleArchive
}) {
  const cardClasses = viewMode === 'list' 
    ? 'mb-2'
    : 'mb-3';

  const cardStyle = viewMode === 'list' 
    ? { minHeight: '80px' }
    : { minHeight: '200px' };

  return (
    <Card className={`${cardClasses} hover:shadow-lg transition-shadow`} style={cardStyle}>
      <CardHeader className={viewMode === 'list' ? 'py-2' : ''}>
        <div className={`flex justify-between items-center ${viewMode === 'list' ? 'mb-1' : 'mb-2'}`}>
          <div className="flex items-center">
            <Link 
              to={`/project-group/${projectGroup.id}`} 
              className={`flex items-center no-underline text-gray-900 dark:text-gray-100 hover:text-red-600 dark:hover:text-red-400 transition-colors ${projectGroup.is_archived ? 'opacity-50' : ''}`}
            >
              <FaFolder className="mr-2" />
              <span className="font-medium">{projectGroup.name}</span>
            </Link>
            {projectGroup.is_favorite && <FaBookmark className="ml-2 text-yellow-500" />}
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
              <FaFolder className="mr-1" />
              {projectGroup.child_groups?.[0]?.count || projectGroup.child_groups?.count || 0}
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
              <FaChartGantt className="mr-1" />
              {projectGroup.projects?.length || projectGroup.projects?.[0]?.count || 0}
            </span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className={viewMode === 'list' ? 'py-2' : ''}>
        {viewMode === 'card' && projectGroup.description && (
          <p className={`mb-4 text-sm ${projectGroup.is_archived ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
            {projectGroup.description}
          </p>
        )}
        
        <div className={`flex justify-between items-center ${viewMode === 'list' ? 'mt-1' : ''}`}>
          <div className="flex items-center gap-1">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddProject?.(projectGroup.id);
              }}
              disabled={projectGroup.is_archived}
              className={`p-2 transition-colors rounded ${projectGroup.is_archived 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-500 hover:text-green-600 hover:bg-green-50 dark:text-gray-400 dark:hover:text-green-400 dark:hover:bg-green-900/20'
              }`}
              title={projectGroup.is_archived ? 'Unarchive to add project' : 'Add Project'}
            >
              <FaPlus />
            </button>
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEdit?.(projectGroup);
              }}
              disabled={projectGroup.is_archived}
              className={`p-2 transition-colors rounded ${projectGroup.is_archived 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-400 dark:hover:text-blue-400 dark:hover:bg-blue-900/20'
              }`}
              title="Edit Project Group"
            >
              <FaEdit />
            </button>
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete?.(projectGroup);
              }}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors rounded"
              title="Delete Project Group"
            >
              <FaTrash />
            </button>
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleFavorite?.(projectGroup.id, projectGroup.is_favorite);
              }}
              className={`p-2 transition-colors rounded ${projectGroup.is_favorite 
                ? 'text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20' 
                : 'text-gray-500 hover:text-yellow-500 hover:bg-yellow-50 dark:text-gray-400 dark:hover:text-yellow-400 dark:hover:bg-yellow-900/20'
              }`}
              title={projectGroup.is_favorite ? 'Remove from Favorites' : 'Add to Favorites'}
            >
              <FaBookmark />
            </button>
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleArchive?.(projectGroup.id, projectGroup.is_archived);
              }}
              className={`p-2 transition-colors rounded ${projectGroup.is_archived 
                ? 'text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20' 
                : 'text-gray-500 hover:text-yellow-500 hover:bg-yellow-50 dark:text-gray-400 dark:hover:text-yellow-400 dark:hover:bg-yellow-900/20'
              }`}
              title={projectGroup.is_archived ? 'Unarchive Group' : 'Archive Group'}
            >
              <FaArchive />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default ProjectGroupCard;
